import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Headset, LogIn, LogOut, Send, CircleCheck, RotateCcw, Inbox } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LogoMark } from "../components/Logo";

function timeAgo(iso) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function SupportLogin() {
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(form.email, form.password);
      if (!u || (u.role !== "support" && u.role !== "admin")) {
        setError("This account does not have support access.");
        return;
      }
      toast.success("Welcome, agent");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-5" data-testid="support-login-page">
      <div className="terminal-grid absolute inset-0 opacity-50" />
      <div className="absolute left-1/2 top-1/3 h-80 w-80 -translate-x-1/2 rounded-full bg-signal/10 blur-[130px]" />
      <form onSubmit={submit} className="relative w-full max-w-md rounded-2xl border border-line bg-surface/90 p-8 backdrop-blur-xl">
        <div className="flex justify-center"><LogoMark size={44} /></div>
        <h1 className="mt-5 text-center font-display text-2xl font-800 tracking-tight text-white">Support Desk</h1>
        <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-muted">Support agents only</p>
        {error && (
          <p className="mt-6 rounded-lg border border-loss/50 bg-loss/10 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.1em] text-loss" data-testid="support-login-error">
            {error}
          </p>
        )}
        <div className="mt-7 space-y-5">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Email</label>
            <input type="email" required autoComplete="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 w-full rounded-xl border border-line bg-ink px-3.5 py-3 font-mono text-sm text-white outline-none focus:border-signal"
              placeholder="support@fundedtraders.club" data-testid="support-login-email" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Password</label>
            <input type="password" required autoComplete="current-password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-2 w-full rounded-xl border border-line bg-ink px-3.5 py-3 font-mono text-sm text-white outline-none focus:border-signal"
              placeholder="••••••••••••" data-testid="support-login-password" />
          </div>
        </div>
        <button type="submit" disabled={busy}
          className="btn-beam btn-gradient mt-8 flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3.5 font-display text-sm font-800 text-white disabled:opacity-50"
          data-testid="support-login-submit">
          <LogIn size={13} /> {busy ? "Authenticating…" : "Open Support Desk"}
        </button>
        <Link to="/" data-testid="support-login-back" className="mt-6 block text-center font-mono text-[10px] uppercase tracking-[0.2em] text-muted transition-colors hover:text-signal">
          ← Back to site
        </Link>
      </form>
    </div>
  );
}

export default function Support() {
  const { user, logout } = useAuth();
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const isAgent = user && (user.role === "support" || user.role === "admin");

  const loadConvs = useCallback(
    () => api.get("/support/agent/conversations").then(({ data }) => setConvs(data)).catch(() => {}),
    []
  );

  useEffect(() => {
    if (!isAgent) return;
    loadConvs();
    const t = setInterval(loadConvs, 5000);
    return () => clearInterval(t);
  }, [isAgent, loadConvs]);

  useEffect(() => {
    if (!isAgent || !activeId) return;
    let stop = false;
    const pull = () =>
      api
        .get(`/support/agent/conversations/${activeId}/messages`)
        .then(({ data }) => {
          if (stop) return;
          setActive(data.conversation);
          setMessages(data.messages);
        })
        .catch(() => {});
    pull();
    const t = setInterval(pull, 4000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [isAgent, activeId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, activeId]);

  if (!isAgent) return <SupportLogin />;

  const send = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !activeId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/support/agent/conversations/${activeId}/messages`, { text: draft.trim() });
      setMessages((m) => [...m, data]);
      setDraft("");
      loadConvs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await api.post(`/support/agent/conversations/${activeId}/status`, { status });
      setActive((a) => ({ ...a, status }));
      loadConvs();
      toast.success(status === "closed" ? "Conversation closed" : "Conversation reopened");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const totalUnread = convs.reduce((s, c) => s + (c.unread_agent || 0), 0);

  return (
    <div className="flex min-h-screen flex-col" data-testid="support-panel">
      <header className="flex items-center justify-between border-b border-line bg-surface/70 px-5 py-3.5 backdrop-blur-xl lg:px-8">
        <div className="flex items-center gap-3">
          <LogoMark size={30} />
          <div>
            <p className="font-display text-sm font-800 text-white">FTC Support Desk</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted">
              {convs.length} conversations · {totalUnread} unread
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden font-mono text-[10px] uppercase tracking-[0.15em] text-muted sm:block" data-testid="support-agent-name">
            {user.name} · {user.role}
          </span>
          <button onClick={logout} className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-muted transition-colors hover:text-white"
            data-testid="support-logout-btn">
            <LogOut size={11} /> Logout
          </button>
        </div>
      </header>

      <div className="mx-auto grid w-full max-w-7xl flex-1 gap-0 px-5 py-6 lg:grid-cols-[320px_1fr] lg:px-8">
        <aside className="overflow-y-auto rounded-2xl border border-line bg-surface/60 lg:mr-5" data-testid="support-inbox">
          <p className="flex items-center gap-2 border-b border-line px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted">
            <Inbox size={12} /> Inbox
          </p>
          {convs.length === 0 && (
            <p className="p-6 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-muted" data-testid="support-inbox-empty">
              No conversations yet
            </p>
          )}
          {convs.map((c) => (
            <button key={c.id} onClick={() => setActiveId(c.id)}
              className={`flex w-full items-start justify-between gap-2 border-b border-line/60 px-4 py-3.5 text-left transition-colors hover:bg-signal/[0.05] ${activeId === c.id ? "bg-signal/[0.08]" : ""}`}
              data-testid={`support-conv-${c.id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-600 text-white">{c.guest_name}</p>
                <p className="truncate font-mono text-[10px] text-muted">{c.guest_email}</p>
                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.12em] text-muted">{timeAgo(c.last_message_at)}</p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                <span className={`rounded-full px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.12em] ${
                  c.status === "open" ? "bg-profit/10 text-profit" : "bg-muted/10 text-muted"
                }`} data-testid={`support-conv-status-${c.id}`}>{c.status}</span>
                {c.unread_agent > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-signal px-1.5 font-mono text-[10px] font-700 text-white"
                    data-testid={`support-unread-${c.id}`}>
                    {c.unread_agent}
                  </span>
                )}
              </div>
            </button>
          ))}
        </aside>

        <section className="mt-5 flex min-h-[520px] flex-col rounded-2xl border border-line bg-surface/60 lg:mt-0" data-testid="support-thread">
          {!active ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
              <Headset size={30} className="text-signal" />
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted" data-testid="support-thread-empty">
                Select a conversation to reply
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3.5">
                <div>
                  <p className="font-display text-sm font-700 text-white" data-testid="support-active-name">{active.guest_name}</p>
                  <p className="font-mono text-[10px] text-muted">{active.guest_email}</p>
                </div>
                {active.status === "open" ? (
                  <button onClick={() => setStatus("closed")}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-loss/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-loss transition-colors hover:bg-loss/10"
                    data-testid="support-close-btn">
                    <CircleCheck size={11} /> Close
                  </button>
                ) : (
                  <button onClick={() => setStatus("open")}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-profit/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-profit transition-colors hover:bg-profit/10"
                    data-testid="support-reopen-btn">
                    <RotateCcw size={11} /> Reopen
                  </button>
                )}
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto p-5" data-testid="support-thread-messages">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "agent" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.sender === "agent"
                        ? "rounded-br-sm bg-signal text-white"
                        : "rounded-bl-sm border border-line bg-ink text-white/90"
                    }`} data-testid={`thread-msg-${m.sender}`}>
                      <p className={`mb-1 font-mono text-[9px] uppercase tracking-[0.15em] ${m.sender === "agent" ? "text-white/70" : "text-signal"}`}>
                        {m.sender_name} · {timeAgo(m.created_at)}
                      </p>
                      {m.text}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={send} className="flex items-center gap-2 border-t border-line p-4">
                <input value={draft} onChange={(e) => setDraft(e.target.value)}
                  placeholder={`Reply to ${active.guest_name}…`}
                  className="w-full rounded-xl border border-line bg-ink px-4 py-3 text-sm text-white outline-none focus:border-signal"
                  data-testid="support-reply-input" />
                <button type="submit" disabled={busy || !draft.trim()}
                  className="btn-gradient flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white disabled:opacity-50"
                  data-testid="support-reply-send" aria-label="Send reply">
                  <Send size={16} />
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
